import type { FragmentData } from "@gqlb/core";
import gsap from "gsap";
import { ArrowRight } from "lucide-react";
import NextLink from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { b } from "@/b";
import { MEDIA_FRAGMENT, ResponsiveImage } from "@/components/ResponsiveImage";

export const HeroProducts_Fragment = b.fragment("HeroProducts", "Query", (b) => [
  b.menu((b) => [
    b.products((b) => [
      b.product((b) => [
        b.id(),
        b.name(),
        b.slug(),
        b.images((b) => [b.__fragment(MEDIA_FRAGMENT)]),
      ]),
      b.callout(),
    ]),
  ]),
]);

type HeroProduct = {
  name: string;
  slug: string;
  image: FragmentData<typeof MEDIA_FRAGMENT>;
};

export const HeroSection: React.FC<{
  data: FragmentData<typeof HeroProducts_Fragment>;
}> = ({ data }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const taglineRef = useRef<HTMLParagraphElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const heroProducts = useMemo<HeroProduct[]>(() => {
    if (!data.menu) return [];
    return data.menu.products
      .filter((p) => p.product.images.length > 0)
      .slice(0, 3)
      .map((p) => ({ name: p.product.name, slug: p.product.slug, image: p.product.images[0] }));
  }, [data.menu]);

  const activeProduct = heroProducts[activeIndex] ?? null;

  useEffect(() => {
    if (heroProducts.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % heroProducts.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroProducts.length]);

  useEffect(() => {
    if (!containerRef.current) return;
    const targets = [headingRef.current, taglineRef.current, buttonsRef.current];
    const ctx = gsap.context(() => {
      gsap.fromTo(
        targets,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.12, ease: "power3.out" },
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  if (heroProducts.length === 0) return null;

  return (
    <section ref={containerRef} className="relative overflow-hidden h-dvh flex flex-col md:block">
      {/* Full-bleed product images */}
      <div className="relative h-[70vh] md:h-full md:absolute md:inset-0 shrink-0">
        {heroProducts.map((product, i) => (
          <div
            key={product.slug}
            className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
            style={{ opacity: i === activeIndex ? 1 : 0 }}
          >
            <ResponsiveImage
              image={product.image}
              objectFit="cover"
              className="absolute inset-0 w-full h-full"
              lazy={i !== 0}
            />
          </div>
        ))}

        {/* Bottom gradient - desktop: text overlay, mobile: fade seam into mirrored reflection */}
        <div className="absolute inset-x-0 bottom-0 hidden md:block h-1/2 bg-gradient-to-t from-black/80 to-transparent z-[1]" />
        <div className="absolute inset-x-0 bottom-0 md:hidden h-48 bg-gradient-to-t from-black/50 to-transparent z-[1]" />
      </div>

      {/* Mirrored reflection below image - mobile only */}
      <div className="relative flex-1 overflow-hidden md:hidden">
        {heroProducts.map((product, i) => (
          <div
            key={`mirror-${product.slug}`}
            className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
            style={{ opacity: i === activeIndex ? 1 : 0, transform: "scaleY(-1)" }}
          >
            <ResponsiveImage
              image={product.image}
              objectFit="cover"
              className="absolute inset-0 w-full h-full"
              lazy
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-xl z-[1]" />
      </div>

      {/* Text - flows below image on mobile, overlaid on desktop */}
      <div className="absolute inset-x-0 bottom-0 z-[2] px-6 md:px-12 py-6 md:pb-10 md:pt-16 flex items-end">
        <div className="max-w-5xl mx-auto w-full flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6">
          <div>
            <h1
              ref={headingRef}
              className="text-4xl sm:text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1]"
            >
              Be a <span className="text-brand-mint">Visionary</span>
            </h1>
            <p
              ref={taglineRef}
              className="mt-2 md:mt-4 text-xs sm:text-lg md:text-xl text-white/80 max-w-lg leading-relaxed"
            >
              Freshly-cooked, gluten-free meals delivered to your nearest pickup location or
              straight to your door.
            </p>
          </div>
          <div ref={buttonsRef} className="flex flex-col items-end gap-3 shrink-0">
            {/* Product callout — matches width of button row */}
            {activeProduct && (
              <NextLink
                href={`/product/${activeProduct.slug}`}
                className="w-full flex items-center justify-between gap-3 bg-white/10 backdrop-blur-md rounded-full pl-5 pr-3 py-2.5 no-underline transition-colors hover:bg-white/20"
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] uppercase tracking-widest text-brand-mint font-semibold">
                    Featured Above
                  </span>
                  <span className="text-xs md:text-sm font-semibold text-white truncate">
                    {activeProduct.name}
                  </span>
                </div>
                <span className="flex items-center gap-1.5 rounded-full bg-brand-mint text-brand-black shrink-0 px-2.5 py-1.5 md:px-3">
                  <span className="text-xs font-semibold hidden md:inline">See this meal</span>
                  <span className="text-xs font-semibold md:hidden">View</span>
                  <ArrowRight className="size-3.5" />
                </span>
              </NextLink>
            )}
            <div className="flex flex-row gap-3 w-full md:w-auto">
              <NextLink
                href="/menu"
                className="flex-1 md:flex-none text-center px-5 py-2.5 md:px-9 md:py-4 bg-brand-mint text-brand-black font-bold rounded-full text-xs md:text-base hover:scale-105 transition-transform"
              >
                View Full Menu
              </NextLink>
              <NextLink
                href="/nutrition"
                className="flex-1 md:flex-none text-center px-5 py-2.5 md:px-9 md:py-4 border border-white/30 text-white font-semibold rounded-full text-xs md:text-base hover:bg-white/10 transition-colors"
              >
                Nutrition Coaching
              </NextLink>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      {heroProducts.length > 1 && (
        <div className="absolute bottom-3 right-6 z-[3] flex gap-1.5">
          {heroProducts.map((product, i) => (
            <button
              key={product.slug}
              type="button"
              onClick={() => setActiveIndex(i)}
              className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                i === activeIndex ? "bg-brand-mint w-8" : "bg-white/50 w-4"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};
